// v41d — Quiet refresh when a new version of the app has been published.
// v88 — the version is the app bundle itself, not the server's start time. Before, /api/version
// reported when the server process started, so every restart (and two servers started at different
// times) looked like a publish and open tabs reloaded, sometimes again and again.
//
// Now: the tab remembers the main script it loaded (/static/js/main.<hash>.js) and checks the
// site's index page every 2 minutes and on focus. Restarts don't change that file; only a publish
// does, and every server serves the same one. When it differs, the tab reloads once on its next
// page change — never while someone is typing or has unsaved changes, and at most once per
// RELOAD_WINDOW_MS for the same tab, so it can never loop.
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackVersionReload } from "@/lib/tracker";

const POLL_MS = 2 * 60 * 1000;
const FIRST_CHECK_DELAY_MS = 5_000;
const RELOAD_WINDOW_MS = 30 * 60 * 1000;
const RELOAD_KEY = "kolradar:version_reload";
const MAIN_RE = /\/static\/js\/(main|bundle)(\.[a-z0-9]+)?\.js/i;

export function loadedBundle() {
  const el = Array.from(document.scripts).find((s) => MAIN_RE.test(s.src || ""));
  return el ? el.src.match(MAIN_RE)[0] : null;
}

async function publishedBundle() {
  try {
    const res = await fetch(`/?__v=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    const m = (await res.text()).match(MAIN_RE);
    return m ? m[0] : null;
  } catch (_) {
    return null;
  }
}

// v88 — typing is recorded as it happens: by the time the route-change effect runs, the page that held
// the focused box is already gone, so reading document.activeElement then always said "not busy".
const TYPING_GRACE_MS = 15_000;
const typing = { at: 0 };
const isTypingTarget = (el) => {
  const tag = (el?.tagName || "").toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !!el?.isContentEditable;
};
if (typeof document !== "undefined") {
  const mark = (e) => { if (isTypingTarget(e.target)) typing.at = Date.now(); };
  ["keydown", "input", "focusin"].forEach((t) => document.addEventListener(t, mark, true));
}

function userIsBusy() {
  try {
    if (isTypingTarget(document.activeElement)) return true;
    if (Date.now() - typing.at < TYPING_GRACE_MS) return true;
    if (document.body?.dataset?.kolradarDirty === "1") return true;
  } catch (_) { /* ignore */ }
  return false;
}

function reloadedRecently() {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    return at && Date.now() - at < RELOAD_WINDOW_MS;
  } catch (_) {
    return true;
  }
}

export function BuildVersionWatcher() {
  const location = useLocation();
  const loaded = useRef(null);
  const latest = useRef(null);
  const lastPathname = useRef(location.pathname);

  useEffect(() => {
    loaded.current = loadedBundle();
    if (!loaded.current) return undefined;
    let cancelled = false;
    const kick = async () => {
      const id = await publishedBundle();
      if (!cancelled && id) latest.current = id;
    };
    const first = setTimeout(kick, FIRST_CHECK_DELAY_MS);
    const timer = setInterval(kick, POLL_MS);
    window.addEventListener("focus", kick);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(timer);
      window.removeEventListener("focus", kick);
    };
  }, []);

  useEffect(() => {
    if (lastPathname.current === location.pathname) return;
    lastPathname.current = location.pathname;
    if (!loaded.current || !latest.current || latest.current === loaded.current) return;
    if (userIsBusy() || reloadedRecently()) return;
    try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch (_) { return; }
    try { trackVersionReload(loaded.current, latest.current); } catch (_) { /* ignore */ }
    window.location.reload();
  }, [location.pathname]);

  return null;
}

export default BuildVersionWatcher;
